import { FormikProps, useFormik } from "formik";
import Modal, { ModalContext, ModalTrigger } from "../../../../../Modal/Modal";
import Button from "@/components/bricks/button/Button";
import EditIcon from "../../../../../icons/EditIcon";
import { FirmaDto } from "../../../../../../interfaces/schema";
import { useContext, useState } from "react";
import Produktionsenhed from "../../../../../wizards/OpretFirma/steps/step1/Produktionsenhed";
import { ISeptimaSearchResultItem } from "../../../../../SeptimaSearchReact/interfaces/types";
import * as Yup from "yup";
import { validationMessages } from "../../../../../../constants/validation";
import { StickyFooter } from "../../../../../stickyFooter/modalStickyFooter";
import { FirmaFields } from "./StamoplysningerTop";
import {
  CvrOpslagDto,
  IProduktionsenhed,
} from "../../../../../../interfaces/firma";
import { useDuplicateFirmaFinder } from "../../../../../../hooks/useDuplicateFirmaFinder";
import DuplicateFirmaWarning from "../../../../../wizards/OpretFirma/steps/DuplicateFirmaWarning";
import { get } from "../../../../../../api/util";
import Spinner from "../../../../../spinner/Spinner";
import InputRadio from "../../../../../formcontrols/InputRadio";
import ValidationErrorMessage from "../../../../../formcontrols/ValidationErrorMessage";
import IsRequired from "../../../../../formcontrols/common/IsRequired";
import {
  EU_VAT_VALIDATION_MESSAGE,
  getVatValidationError,
  isKnownVatNumber,
  normalizeVatNumber,
} from "../../../../../../util/vatValidation";
import {
  getFirmaRegistrationNumber,
  hasFirmaRegistrationNumber,
} from "../../../../../../util/firmaRegistrationNumber";

type CvrInputMode = "cvr" | "vat";

interface IFirmaStamCVRModalProps {
  firma: FirmaDto;
  main: FormikProps<FirmaFields>;
  editMode: boolean;
}

function ModalContent(props: IFirmaStamCVRModalProps) {
  const { main } = props;
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<
    ISeptimaSearchResultItem | undefined
  >(undefined);
  const [produktionsEnheder, setProduktionsEnheder] = useState<
    IProduktionsenhed[]
  >([]);
  const { setModalOpen } = useContext(ModalContext);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      inputMode: "cvr" as CvrInputMode,
      vatNumber: "",
      produktionsenhed: undefined,
      navn: "",
      isSearchable: true,
    } as {
      inputMode: CvrInputMode;
      vatNumber: string;
      produktionsenhed: IProduktionsenhed | undefined;
      navn: string;
      isSearchable: boolean;
    },
    validationSchema: Yup.object({
      inputMode: Yup.string().oneOf(["cvr", "vat"]).required(),
      vatNumber: Yup.string().when("inputMode", {
        is: "vat",
        then: (schema) =>
          schema
            .required(validationMessages.required)
            .test("valid-vat", EU_VAT_VALIDATION_MESSAGE, (value) =>
              isKnownVatNumber(value ?? ""),
            ),
      }),
      produktionsenhed: Yup.object().when("inputMode", {
        is: "cvr",
        then: (schema) =>
          schema.shape({
            cvrnummer: Yup.string().required(validationMessages.required),
            pnummer: Yup.string().required(validationMessages.required),
          }),
      }),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        if (values.inputMode === "vat") {
          main.setValues({
            ...main.values,
            udenlandskVirksomhedsId: normalizeVatNumber(values.vatNumber),
          });
        } else if (values.produktionsenhed) {
          const { beliggenhedsadresse, navn } = await get<CvrOpslagDto>({
            query: `grunddata/cvr?cvrNr=${values.produktionsenhed.cvrnummer}&pNr=${values.produktionsenhed.pnummer}`,
          });
          const cvrNr = values.produktionsenhed.cvrnummer;
          const pnr = values.produktionsenhed.pnummer;
          const navn1 = navn;
          main.setValues({
            ...main.values,
            navn1,
            pnr,
            cvrNr,
            beliggenhedsadresse,
          });
        }
        setModalOpen(false);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        setIsLoading(false);
        setError("Kunne ikke føje til firma");
      }
    },
  });
  const { handleSubmit, resetForm, values, setFieldValue, touched, errors } =
    formik;
  const existingFirma = useDuplicateFirmaFinder(
    values.inputMode === "cvr" && formik.values.isSearchable,
    formik.values.produktionsenhed,
  );
  const vatError = getVatValidationError(values.vatNumber);
  const canSubmit =
    values.inputMode === "vat"
      ? values.vatNumber.length > 0 && !vatError
      : !!values.produktionsenhed?.cvrnummer;

  const handleInputModeChange = (mode: CvrInputMode) => {
    setFieldValue("inputMode", mode, false);
    setFieldValue("vatNumber", "", false);
    setFieldValue("produktionsenhed", undefined, false);
    setSearchResult(undefined);
    setProduktionsEnheder([]);
    setError(undefined);
  };

  return (
    <Modal
      title={
        <>
          <EditIcon /> Angiv CVR- eller VAT-nr.
        </>
      }
      description=""
    >
      <div className="c-modal-container">
        <form autoComplete="off" onSubmit={handleSubmit}>
          <div className="c-item-list c-modal-container-inner">
            <div className="w-100">
              <label className="form-label">Type nummer</label>
              <div className="mt-2 d-flex flex-row flex-nowrap align-items-center gap-2">
                <InputRadio
                  className="form-check--badge flex-shrink-0"
                  label="Dansk CVR"
                  name="inputMode"
                  id="inputMode-cvr"
                  checked={values.inputMode === "cvr"}
                  onChange={() => handleInputModeChange("cvr")}
                />
                <InputRadio
                  className="form-check--badge flex-shrink-0"
                  label="Udenlandsk VAT-nr."
                  name="inputMode"
                  id="inputMode-vat"
                  checked={values.inputMode === "vat"}
                  onChange={() => handleInputModeChange("vat")}
                />
              </div>
            </div>
            {values.inputMode === "cvr" && (
              <Produktionsenhed
                formik={formik}
                produktionsEnheder={produktionsEnheder}
                searchResult={searchResult}
                setProduktionsEnheder={setProduktionsEnheder}
                setSearchResult={setSearchResult}
              />
            )}
            {values.inputMode === "vat" && (
              <div className="w-100 position-relative">
                <label className="form-label" htmlFor="vat-number">
                  VAT-nr. <IsRequired />
                </label>
                <input
                  type="text"
                  className={`form-control${
                    touched.vatNumber && errors.vatNumber ? " is-invalid" : ""
                  }`}
                  id="vat-number"
                  name="vatNumber"
                  value={values.vatNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="fx DE123456789"
                />
                <ValidationErrorMessage
                  touched={touched.vatNumber}
                  errorMessage={errors.vatNumber}
                />
              </div>
            )}
            {existingFirma && (
              <DuplicateFirmaWarning
                existingFirma={existingFirma.firma}
                onCancel={() => {
                  setModalOpen(false);
                  resetForm();
                }}
                reason={existingFirma.reason}
              />
            )}
          </div>
          <StickyFooter>
            <Button
              type="button"
              color="secondary"
              onClick={() => setModalOpen(false)}
            >
              Annuller
            </Button>
            <span className="d-flex gap-2 align-items-center">
              {isLoading && <Spinner size={24} />}
              {error && (
                <small className={`text-danger mt-1 fw-semibold`}>
                  {error}
                </small>
              )}
              <Button
                type="submit"
                color="primary"
                onClick={() => {}}
                disabled={formik.isSubmitting || !canSubmit || !!existingFirma}
              >
                Færdig
              </Button>
            </span>
          </StickyFooter>
        </form>
      </div>
    </Modal>
  );
}

const FirmaStamCVRModal = (props: IFirmaStamCVRModalProps) => {
  const { editMode, firma, main } = props;
  const { modalOpen } = useContext(ModalContext);
  const registrationSource = { ...firma, ...main.values };
  const registrationNumber = getFirmaRegistrationNumber(registrationSource);
  const hasRegistrationNumber = hasFirmaRegistrationNumber(registrationSource);

  return (
    <>
      {editMode && (
        <>
          {!hasRegistrationNumber && (
            <ModalTrigger asChild>
              <Button
                type="button"
                className="btn--action w-100 fw-medium text-dark py-2 justify-content-between"
                color="transparent"
                onClick={() => {}}
              >
                <span>{registrationNumber ?? "Angiv CVR- eller VAT-nr."}</span>
                <span className="text-primary">
                  <EditIcon />
                </span>
              </Button>
            </ModalTrigger>
          )}
          {hasRegistrationNumber && (
            <div className="form-control--value">{registrationNumber}</div>
          )}
        </>
      )}
      <ModalContent key={modalOpen.toString()} {...props} />
    </>
  );
};

export default FirmaStamCVRModal;
